import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SmartText } from "./SmartText";
import { celebrate } from "../utils/celebrate";
import { ErrorBoundary } from "./shared/ui";
import { scheduleReview, isDue, recordResult } from "../lib/reviewScheduler";

const WidgetRenderer = lazy(() =>
  import("../widgets/WidgetRenderer").then(m => ({ default: m.WidgetRenderer }))
);

// ── Age-mode token sets (from prototypes) ─────────────────────────────
const AGE_TOKENS = {
  playful: {
    bg: "#FFF6EA", surface: "#FFFFFF", card: "#FFFAF3",
    border: "#FFD9B2", text: "#2D1800", textMid: "#7A4520", textDim: "#C47A3A",
    kicker: null,
    radius: 26, headingFont: "'Baloo 2', 'Plus Jakarta Sans', cursive",
    bodyFont: "'Hanken Grotesk', 'Plus Jakarta Sans', sans-serif",
    labelFont: "'Space Mono', monospace", dark: false,
  },
  friendly: {
    bg: "#F5F3EF", surface: "#FFFFFF", card: "#FFFFFF",
    border: "#D9D3C8", text: "#1A1714", textMid: "#4A4340", textDim: "#8A8380",
    kicker: null,
    radius: 20, headingFont: "'Baloo 2', 'Plus Jakarta Sans', cursive",
    bodyFont: "'Hanken Grotesk', 'Plus Jakarta Sans', sans-serif",
    labelFont: "'Space Mono', monospace", dark: false,
  },
  focused: {
    bg: "#F7F5F1", surface: "#FFFFFF", card: "#FFFFFF",
    border: "#E2DDD5", text: "#1A1714", textMid: "#4A4340", textDim: "#8A8380",
    kicker: null,
    radius: 16, headingFont: "'Space Grotesk', 'Plus Jakarta Sans', sans-serif",
    bodyFont: "'Hanken Grotesk', 'Plus Jakarta Sans', sans-serif",
    labelFont: "'Space Mono', monospace", dark: false,
  },
  rigorous: {
    bg: "#12141C", surface: "#1B1E28", card: "#1B1E28",
    border: "#2C3048", text: "#E8EEFF", textMid: "#8A9AC0", textDim: "#5A6A90",
    kicker: null,
    radius: 14, headingFont: "'Space Grotesk', 'Plus Jakarta Sans', sans-serif",
    bodyFont: "'Hanken Grotesk', 'Plus Jakarta Sans', sans-serif",
    labelFont: "'Space Mono', monospace", dark: true,
  },
  warm_light: {
    bg:      "#FBFAF6",
    surface: "#F0EDE6",
    card:    "#FFFFFF",
    border:  "#ECE7DB",
    text:    "#1B1B23",
    textMid: "#57606A",
    textDim: "#6B7280",
    kicker:  "#0E9488",
    radius:  14,
    headingFont: "'Space Grotesk', 'Plus Jakarta Sans', sans-serif",
    bodyFont:    "'Hanken Grotesk', 'Plus Jakarta Sans', sans-serif",
    labelFont:   "'Space Mono', monospace",
    dark: false,
  },
};

// ── Helpers ────────────────────────────────────────────────────────────
function getAgeTokens(presentationMode) {
  return AGE_TOKENS[presentationMode] || AGE_TOKENS.focused;
}

// snake_case → PascalCase for widget type lookup
function normalizeWidgetType(type) {
  if (!type) return null;
  return type.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join("");
}

function isGated(screenType) {
  return ["predict", "check", "construct", "challenge"].includes(screenType);
}

function eyebrow(tok, accent) {
  return tok.kicker || accent;
}

// ── LessonChrome ───────────────────────────────────────────────────────
function LessonChrome({ screens, screenIndex, lesson, onClose, tok, a11ySize, onToggleA11y }) {
  const total = screens.length;
  const mins = lesson.estimated_minutes ?? Math.max(1, Math.round(total * 0.6));

  return (
    <div style={{
      padding: "12px 16px 10px",
      background: tok.surface,
      borderBottom: `1px solid ${tok.border}`,
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <button
          onClick={onClose}
          aria-label="Close lesson"
          style={{
            background: tok.card, border: `1px solid ${tok.border}`,
            borderRadius: 8, width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: tok.textMid, cursor: "pointer", flexShrink: 0, fontSize: 14,
          }}
        >←</button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 17 }}>{lesson.icon}</span>
            <h2 style={{
              fontSize: 14, fontWeight: 700, color: tok.text, margin: 0,
              fontFamily: tok.headingFont, lineHeight: 1.3,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {lesson.title}
            </h2>
          </div>
          <span style={{ fontSize: 11, color: tok.textMid, fontFamily: tok.labelFont }}>
            {mins} min · {lesson.year?.replace("year", "Year ")}
          </span>
        </div>

        <button
          onClick={onToggleA11y}
          aria-label="Accessibility options"
          title="Text size"
          style={{
            background: tok.card, border: `1px solid ${tok.border}`,
            borderRadius: 8, width: 32, height: 32,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: tok.textMid, cursor: "pointer", fontSize: 15,
          }}
        >Aa</button>
      </div>

      {/* Progress dots */}
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        {screens.map((_, i) => (
          <div
            key={i}
            aria-hidden="true"
            style={{
              height: 5, borderRadius: 99,
              width: i === screenIndex ? 20 : 5,
              background: i <= screenIndex ? "#14B8A6" : "#E3E0D8",
              transition: "all .25s",
            }}
          />
        ))}
        <span style={{
          fontSize: 10, color: tok.textDim, marginLeft: 6,
          fontFamily: tok.labelFont,
        }}>
          {screenIndex + 1}/{total}
        </span>
      </div>
    </div>
  );
}

// ── ReviewWarmup ───────────────────────────────────────────────────────
function ReviewWarmup({ lesson, tok, onDone }) {
  const prompts = lesson.review?.review_prompts ?? [];
  const [idx, setIdx] = useState(0);
  const [value, setValue] = useState("");
  const [result, setResult] = useState(null); // null | 'correct' | 'wrong'
  const conceptId = lesson.review?.concept_id;

  function check() {
    const expected = Number(prompts[idx]?.answer);
    const isCorrect = Math.abs(Number(value) - expected) <= 0;
    setResult(isCorrect ? "correct" : "wrong");
    if (conceptId) recordResult(conceptId, isCorrect);
  }

  function next() {
    if (idx + 1 >= prompts.length) {
      onDone();
    } else {
      setIdx(i => i + 1);
      setValue("");
      setResult(null);
    }
  }

  if (!prompts.length) { onDone(); return null; }
  const prompt = prompts[idx];
  const accent = lesson.color || "#6C63FF";

  return (
    <div style={{ padding: "24px 20px", flex: 1, overflowY: "auto" }}>
      <p style={{
        fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
        textTransform: "uppercase", letterSpacing: ".1em",
        fontFamily: tok.labelFont, margin: "0 0 16px",
      }}>Quick Review · {idx + 1}/{prompts.length}</p>

      <div style={{
        background: `${accent}12`, border: `1.5px solid ${accent}30`,
        borderRadius: tok.radius, padding: "20px 18px", marginBottom: 20,
      }}>
        <p style={{ fontSize: 16, color: tok.text, margin: 0, lineHeight: 1.6, fontFamily: tok.bodyFont }}>
          <SmartText>{prompt.prompt}</SmartText>
        </p>
      </div>

      {result === null ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            type="number"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") check(); }}
            aria-label="Your answer"
            style={{
              width: 96, height: 54, fontSize: 22, fontWeight: 700, textAlign: "center",
              border: `2px solid ${tok.border}`, borderRadius: 13, background: tok.card,
              color: tok.text, outline: "none",
            }}
          />
          <button
            onClick={check}
            disabled={!value}
            style={{
              background: accent, color: "white", border: "none", borderRadius: 11,
              padding: "14px 22px", fontWeight: 700, fontSize: 14, cursor: value ? "pointer" : "not-allowed",
              opacity: value ? 1 : 0.5,
            }}
          >Check</button>
        </div>
      ) : (
        <div>
          <div style={{
            padding: "12px 16px", borderRadius: 12, marginBottom: 14,
            background: result === "correct" ? "#ECFDF5" : "#FFF1F2",
            border: `1px solid ${result === "correct" ? "#10B98150" : "#EF444450"}`,
          }}>
            <p style={{
              margin: 0, fontWeight: 700, color: result === "correct" ? "#059669" : "#DC2626",
              fontFamily: tok.bodyFont,
            }}>
              {result === "correct" ? "✓ Correct" : `Not quite — the answer is ${prompt.answer}`}
            </p>
          </div>
          <button
            onClick={next}
            style={{
              width: "100%", padding: "13px", background: accent, color: "white",
              border: "none", borderRadius: 11, fontWeight: 700, fontSize: 14, cursor: "pointer",
            }}
          >{idx + 1 >= prompts.length ? "Start lesson →" : "Next prompt →"}</button>
        </div>
      )}
    </div>
  );
}

// ── ConceptScreen ──────────────────────────────────────────────────────
function ConceptScreen({ screen, lesson, tok, accent, a11ySize, isFirst }) {
  return (
    <div>
      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 14px",
        }}>{screen.eyebrow}</p>
      )}

      <h1 style={{
        fontSize: 20 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 12px", lineHeight: 1.3, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h1>

      {screen.body && (
        <p style={{
          fontSize: 15 + a11ySize, color: tok.textMid, margin: "0 0 18px",
          lineHeight: 1.7, fontFamily: tok.bodyFont,
        }}>
          <SmartText>{screen.body}</SmartText>
        </p>
      )}

      {/* why_it_matters on concept intro */}
      {isFirst && lesson.why_it_matters && (
        <div style={{
          background: "#EEF6F0", border: "1px solid #CFE7D8",
          borderRadius: tok.radius, padding: "14px 16px", marginBottom: 18,
        }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#1F8A5B", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: ".06em" }}>
            Why this matters
          </p>
          <p style={{ fontSize: 14 + a11ySize, color: "#1A4030", margin: 0, lineHeight: 1.65, fontFamily: tok.bodyFont }}>
            <SmartText>{lesson.why_it_matters}</SmartText>
          </p>
        </div>
      )}

      <ScreenWidget screen={screen} lesson={lesson} tok={tok} accent={accent} />
    </div>
  );
}

// ── PredictScreen ─────────────────────────────────────────────────────
function PredictScreen({ screen, tok, accent, a11ySize, screenState, onAnswer }) {
  const { selected, attempts, solved, walkthroughOpen } = screenState;

  return (
    <div>
      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 10px",
        }}>{screen.eyebrow}</p>
      )}
      <h2 style={{
        fontSize: 18 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 8px", lineHeight: 1.35, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h2>
      {screen.body && (
        <p style={{ fontSize: 14 + a11ySize, color: tok.textMid, margin: "0 0 16px", lineHeight: 1.65, fontFamily: tok.bodyFont }}>
          <SmartText>{screen.body}</SmartText>
        </p>
      )}
      {screen.question && (
        <p style={{ fontSize: 15 + a11ySize, fontWeight: 600, color: tok.text, margin: "0 0 14px", lineHeight: 1.6, fontFamily: tok.bodyFont }}>
          <SmartText>{screen.question}</SmartText>
        </p>
      )}

      <OptionsGroup
        options={screen.options}
        selected={selected}
        solved={solved}
        tok={tok}
        accent={accent}
        a11ySize={a11ySize}
        onSelect={onAnswer}
      />

      <WalkthroughReveal
        screen={screen}
        attempts={attempts}
        walkthroughOpen={walkthroughOpen}
        tok={tok}
        accent={accent}
        a11ySize={a11ySize}
        onOpen={() => onAnswer(null, true)}
      />
    </div>
  );
}

// ── ExploreScreen ─────────────────────────────────────────────────────
function ExploreScreen({ screen, lesson, tok, accent, a11ySize }) {
  return (
    <div>
      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 10px",
        }}>{screen.eyebrow}</p>
      )}
      <h2 style={{
        fontSize: 18 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 8px", lineHeight: 1.35, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h2>
      {screen.body && (
        <p style={{ fontSize: 14 + a11ySize, color: tok.textMid, margin: "0 0 18px", lineHeight: 1.65, fontFamily: tok.bodyFont }}>
          <SmartText>{screen.body}</SmartText>
        </p>
      )}
      <ScreenWidget screen={screen} lesson={lesson} tok={tok} accent={accent} />
    </div>
  );
}

// ── RevealScreen ──────────────────────────────────────────────────────
function RevealScreen({ screen, lesson, tok, accent, a11ySize }) {
  return (
    <div>
      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 10px",
        }}>{screen.eyebrow}</p>
      )}
      <h2 style={{
        fontSize: 18 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 12px", lineHeight: 1.35, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h2>
      {screen.body && (
        <div style={{
          background: `${accent}10`, border: `1.5px solid ${accent}30`,
          borderRadius: tok.radius, padding: "18px 20px", marginBottom: 18,
        }}>
          <p style={{ fontSize: 15 + a11ySize, color: tok.text, margin: 0, lineHeight: 1.75, fontFamily: tok.bodyFont }}>
            <SmartText>{screen.body}</SmartText>
          </p>
        </div>
      )}
      <ScreenWidget screen={screen} lesson={lesson} tok={tok} accent={accent} />
    </div>
  );
}

// ── CheckScreen ───────────────────────────────────────────────────────
function CheckScreen({ screen, tok, accent, a11ySize, screenState, onAnswer }) {
  const { selected, attempts, solved, walkthroughOpen } = screenState;

  return (
    <div>
      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 10px",
        }}>{screen.eyebrow}</p>
      )}
      <h2 style={{
        fontSize: 18 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 8px", lineHeight: 1.35, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h2>
      {screen.question && (
        <p style={{ fontSize: 15 + a11ySize, fontWeight: 600, color: tok.text, margin: "0 0 14px", lineHeight: 1.6, fontFamily: tok.bodyFont }}>
          <SmartText>{screen.question}</SmartText>
        </p>
      )}

      <OptionsGroup
        options={screen.options}
        selected={selected}
        solved={solved}
        tok={tok}
        accent={accent}
        a11ySize={a11ySize}
        onSelect={onAnswer}
      />

      <WalkthroughReveal
        screen={screen}
        attempts={attempts}
        walkthroughOpen={walkthroughOpen}
        tok={tok}
        accent={accent}
        a11ySize={a11ySize}
        onOpen={() => onAnswer(null, true)}
      />
    </div>
  );
}

// ── ConstructScreen ───────────────────────────────────────────────────
function ConstructScreen({ screen, tok, accent, a11ySize, screenState, onAnswer }) {
  const { attempts, solved, walkthroughOpen } = screenState;
  const [value, setValue] = useState("");
  const [checked, setChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const expected = screen.answer?.value ?? 0;
  const tolerance = screen.answer?.tolerance ?? 0;
  const unit = screen.answer?.unit ?? "";

  function checkAnswer() {
    const num = Number(value);
    const correct = Math.abs(num - expected) <= tolerance;
    setChecked(true);
    setIsCorrect(correct);
    onAnswer(correct ? 0 : -1, false, correct); // pass correctness up
  }

  // localSolved: student got it right in this visit (requires explicit Check press)
  // screenState.solved: gates the Continue button (handled by parent canContinue())
  const localSolved = checked && isCorrect;

  return (
    <div>
      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 10px",
        }}>{screen.eyebrow}</p>
      )}
      <h2 style={{
        fontSize: 18 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 12px", lineHeight: 1.35, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h2>
      {screen.body && (
        <p style={{ fontSize: 14 + a11ySize, color: tok.textMid, margin: "0 0 18px", lineHeight: 1.65, fontFamily: tok.bodyFont }}>
          <SmartText>{screen.body}</SmartText>
        </p>
      )}

      {!localSolved && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
          <div>
            <input
              type="number"
              value={value}
              onChange={e => { setValue(e.target.value); setChecked(false); }}
              onKeyDown={e => { if (e.key === "Enter" && value) checkAnswer(); }}
              aria-label={`Enter your answer${unit ? ` in ${unit}` : ""}`}
              style={{
                width: 96, height: 54, fontSize: 24, fontWeight: 700, textAlign: "center",
                border: `2px solid ${checked ? (isCorrect ? "#10B981" : "#EF4444") : tok.border}`,
                borderRadius: 13, background: tok.card, color: tok.text, outline: "none",
                fontFamily: tok.bodyFont,
              }}
            />
            {unit && <span style={{ fontSize: 12, color: tok.textDim, marginLeft: 6, fontFamily: tok.labelFont }}>{unit}</span>}
          </div>
          <button
            onClick={checkAnswer}
            disabled={!value}
            style={{
              background: accent, color: "white", border: "none", borderRadius: 11,
              padding: "14px 22px", fontWeight: 700, fontSize: 14,
              cursor: value ? "pointer" : "not-allowed", opacity: value ? 1 : 0.5,
            }}
          >Check</button>
        </div>
      )}

      {checked && (
        <div style={{
          padding: "12px 16px", borderRadius: 12, marginBottom: 16,
          background: isCorrect ? "#ECFDF5" : "#FFF1F2",
          border: `1px solid ${isCorrect ? "#10B98150" : "#EF444450"}`,
        }}>
          <p style={{ margin: 0, fontWeight: 700, color: isCorrect ? "#059669" : "#DC2626", fontFamily: tok.bodyFont }}>
            {isCorrect ? `✓ Correct — ${value}${unit ? " " + unit : ""}` : `Not quite — try again`}
          </p>
        </div>
      )}

      <WalkthroughReveal
        screen={screen}
        attempts={attempts}
        walkthroughOpen={walkthroughOpen}
        tok={tok}
        accent={accent}
        a11ySize={a11ySize}
        onOpen={() => onAnswer(null, true)}
      />
    </div>
  );
}

// ── WorkedScreen ──────────────────────────────────────────────────────
function WorkedScreen({ screen, tok, accent, a11ySize }) {
  const steps = screen.widget_config?.steps ?? screen.steps ?? [];
  const finalAnswer = screen.widget_config?.answer ?? screen.answer ?? null;
  const [revealed, setRevealed] = useState(0);
  const allDone = revealed >= steps.length;

  return (
    <div>
      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 10px",
        }}>{screen.eyebrow}</p>
      )}
      <h2 style={{
        fontSize: 18 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 8px", lineHeight: 1.35, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h2>
      {screen.body && (
        <p style={{ fontSize: 14 + a11ySize, color: tok.textMid, margin: "0 0 18px", lineHeight: 1.65, fontFamily: tok.bodyFont }}>
          <SmartText>{screen.body}</SmartText>
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
        {steps.slice(0, revealed).map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
          >
            <span style={{
              width: 26, height: 26, borderRadius: "50%",
              background: `${accent}22`, color: accent,
              fontSize: 12, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, marginTop: 2,
            }}>{i + 1}</span>
            <p style={{ fontSize: 14 + a11ySize, color: tok.textMid, margin: 0, lineHeight: 1.65, fontFamily: tok.bodyFont }}>
              <SmartText>{step}</SmartText>
            </p>
          </motion.div>
        ))}
      </div>

      {allDone && finalAnswer && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: `${accent}15`, border: `1.5px solid ${accent}44`,
            borderRadius: tok.radius, padding: "12px 16px", marginBottom: 16,
          }}
        >
          <p style={{ fontSize: 11, fontWeight: 800, color: accent, margin: "0 0 4px", textTransform: "uppercase", letterSpacing: ".06em", fontFamily: tok.labelFont }}>Answer</p>
          <p style={{ fontSize: 15, fontWeight: 700, color: tok.text, margin: 0, lineHeight: 1.6, fontFamily: tok.bodyFont }}>
            <SmartText>{typeof finalAnswer === "string" ? finalAnswer : String(finalAnswer)}</SmartText>
          </p>
        </motion.div>
      )}

      {!allDone && steps.length > 0 && (
        <button
          onClick={() => setRevealed(r => r + 1)}
          style={{
            background: accent, border: "none", borderRadius: 11, color: "white",
            padding: "10px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer",
            fontFamily: tok.bodyFont,
          }}
        >
          {revealed === 0 ? "Show step 1 →" : `Step ${revealed + 1} of ${steps.length} →`}
        </button>
      )}
    </div>
  );
}

// ── ChallengeScreen ───────────────────────────────────────────────────
function ChallengeScreen({ screen, lesson, tok, accent, a11ySize, screenState, onAnswer }) {
  const { selected, attempts, solved, walkthroughOpen } = screenState;
  const hasOptions = screen.options?.length > 0;
  const hasConstruct = screen.answer != null && !hasOptions;

  // Numeric-input state (used only in construct path)
  const [value, setValue] = React.useState("");
  const [checked, setChecked] = React.useState(false);
  const [isCorrect, setIsCorrect] = React.useState(false);

  const expected = screen.answer?.value ?? 0;
  const tolerance = screen.answer?.tolerance ?? 0;
  const unit = screen.answer?.unit ?? "";

  function checkAnswer() {
    const num = Number(value);
    const correct = Math.abs(num - expected) <= tolerance;
    setChecked(true);
    setIsCorrect(correct);
    onAnswer(correct ? 0 : -1, false, correct);
  }

  const localSolved = checked && isCorrect;

  return (
    <div>
      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 10px",
        }}>{screen.eyebrow}</p>
      )}
      <h2 style={{
        fontSize: 18 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 8px", lineHeight: 1.35, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h2>
      {screen.body && (
        <p style={{ fontSize: 14 + a11ySize, color: tok.textMid, margin: "0 0 18px", lineHeight: 1.65, fontFamily: tok.bodyFont }}>
          <SmartText>{screen.body}</SmartText>
        </p>
      )}

      <ScreenWidget screen={screen} lesson={lesson} tok={tok} accent={accent} />

      {hasOptions && (
        <OptionsGroup
          options={screen.options}
          selected={selected}
          solved={solved}
          tok={tok}
          accent={accent}
          a11ySize={a11ySize}
          onSelect={onAnswer}
        />
      )}

      {hasConstruct && !localSolved && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
          <div>
            <input
              type="number"
              value={value}
              onChange={e => { setValue(e.target.value); setChecked(false); }}
              onKeyDown={e => { if (e.key === "Enter" && value) checkAnswer(); }}
              aria-label={`Enter your answer${unit ? ` in ${unit}` : ""}`}
              style={{
                width: 96, height: 54, fontSize: 24, fontWeight: 700, textAlign: "center",
                border: `2px solid ${checked ? (isCorrect ? "#10B981" : "#EF4444") : tok.border}`,
                borderRadius: 13, background: tok.card, color: tok.text, outline: "none",
                fontFamily: tok.bodyFont,
              }}
            />
            {unit && <span style={{ fontSize: 12, color: tok.textDim, marginLeft: 6, fontFamily: tok.labelFont }}>{unit}</span>}
          </div>
          <button
            onClick={checkAnswer}
            disabled={!value}
            style={{
              background: accent, color: "white", border: "none", borderRadius: 11,
              padding: "14px 22px", fontWeight: 700, fontSize: 14,
              cursor: value ? "pointer" : "not-allowed", opacity: value ? 1 : 0.5,
            }}
          >Check</button>
        </div>
      )}

      {hasConstruct && checked && (
        <div style={{
          padding: "12px 16px", borderRadius: 12, marginBottom: 16,
          background: isCorrect ? "#ECFDF5" : "#FFF1F2",
          border: `1px solid ${isCorrect ? "#10B98150" : "#EF444450"}`,
        }}>
          <p style={{ margin: 0, fontWeight: 700, color: isCorrect ? "#059669" : "#DC2626", fontFamily: tok.bodyFont }}>
            {isCorrect ? `✓ Correct — ${value}${unit ? " " + unit : ""}` : "Not quite — try again"}
          </p>
        </div>
      )}

      <WalkthroughReveal
        screen={screen}
        attempts={attempts}
        walkthroughOpen={walkthroughOpen}
        tok={tok}
        accent={accent}
        a11ySize={a11ySize}
        onOpen={() => onAnswer(null, true)}
      />
    </div>
  );
}

// ── CelebrateScreen ───────────────────────────────────────────────────
function CelebrateScreen({ screen, lesson, tok, accent, a11ySize, rewardTone }) {
  const isPlayful = rewardTone === "playful";

  return (
    <div style={{ textAlign: "center", padding: "8px 0" }}>
      {isPlayful ? (
        <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
      ) : (
        <div style={{
          width: 60, height: 60, borderRadius: "50%", margin: "0 auto 16px",
          background: `radial-gradient(circle at 35% 30%, ${accent}88, ${accent} 60%, ${accent}CC)`,
          boxShadow: `0 0 24px ${accent}66`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 28,
        }}>✓</div>
      )}

      {screen.eyebrow && (
        <p style={{
          fontSize: 10, fontWeight: 700, color: eyebrow(tok, accent),
          textTransform: "uppercase", letterSpacing: ".1em",
          fontFamily: tok.labelFont, margin: "0 0 10px",
        }}>{screen.eyebrow}</p>
      )}
      <h2 style={{
        fontSize: 22 + a11ySize, fontWeight: 700, color: tok.text,
        margin: "0 0 10px", lineHeight: 1.3, fontFamily: tok.headingFont,
      }}>
        <SmartText>{screen.title}</SmartText>
      </h2>
      {screen.body && (
        <p style={{ fontSize: 14 + a11ySize, color: tok.textMid, margin: "0 0 20px", lineHeight: 1.7, fontFamily: tok.bodyFont }}>
          <SmartText>{screen.body}</SmartText>
        </p>
      )}

      {isPlayful ? (
        <div style={{ fontSize: 14, color: tok.textMid, fontFamily: tok.bodyFont }}>
          Amazing work! Keep that streak going! 🌟
        </div>
      ) : (
        <div style={{
          display: "inline-block", background: `${accent}18`,
          border: `1px solid ${accent}40`, borderRadius: 20,
          padding: "6px 16px", fontSize: 13, color: accent,
          fontWeight: 600, fontFamily: tok.labelFont,
        }}>
          Added to review schedule
        </div>
      )}
    </div>
  );
}

// ── Shared: OptionsGroup ─────────────────────────────────────────────
function OptionsGroup({ options, selected, solved, tok, accent, a11ySize, onSelect }) {
  if (!options?.length) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      {options.map((opt, idx) => {
        const isSelected = selected === idx;
        const isCorrect = opt.correct === true;
        const showResult = selected !== null;
        const showFeedback = isSelected && showResult;

        let bg = tok.card;
        let border = tok.border;
        let textColor = tok.text;
        let icon = null;

        if (showResult) {
          if (isCorrect && (isSelected || solved)) {
            bg = "#ECFDF5"; border = "#10B981"; textColor = "#059669"; icon = "✓";
          } else if (isSelected && !isCorrect) {
            bg = "#FFF1F2"; border = "#EF4444"; textColor = "#DC2626"; icon = "✗";
          }
        }

        return (
          <div key={idx}>
            <button
              onClick={() => !solved && onSelect(idx)}
              disabled={solved && !isCorrect}
              aria-pressed={isSelected}
              style={{
                width: "100%", background: bg, border: `1.5px solid ${border}`,
                borderRadius: 12, padding: "12px 16px",
                textAlign: "left", cursor: solved ? "default" : "pointer",
                color: textColor, fontSize: 14 + a11ySize,
                fontFamily: tok.bodyFont, lineHeight: 1.5,
                display: "flex", alignItems: "center", gap: 10,
                transition: "all .15s",
              }}
            >
              {icon && <span style={{ flexShrink: 0, fontWeight: 700 }}>{icon}</span>}
              <SmartText>{opt.text}</SmartText>
            </button>

            {/* Per-option Socratic feedback (#1) */}
            <AnimatePresence>
              {showFeedback && opt.feedback && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{
                    margin: "5px 0 0 0",
                    padding: "10px 14px",
                    background: isCorrect ? "#ECFDF520" : "#FFF1F220",
                    border: `1px solid ${isCorrect ? "#10B98140" : "#EF444440"}`,
                    borderRadius: "0 0 10px 10px",
                    fontSize: 13 + a11ySize,
                    color: isCorrect ? "#059669" : tok.textMid,
                    lineHeight: 1.6,
                    fontFamily: tok.bodyFont,
                  }}>
                    <SmartText>{opt.feedback}</SmartText>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

// ── Shared: WalkthroughReveal (#2) ────────────────────────────────────
function WalkthroughReveal({ screen, attempts, walkthroughOpen, tok, accent, a11ySize, onOpen }) {
  const walkthrough = screen.walkthrough ?? [];
  if (!walkthrough.length) return null;
  if (attempts < 2 && !walkthroughOpen) return null;

  return (
    <div style={{ marginTop: 16 }}>
      {!walkthroughOpen ? (
        <button
          onClick={onOpen}
          style={{
            background: "none", border: `1.5px dashed ${accent}80`,
            borderRadius: 10, padding: "10px 16px", color: accent,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            fontFamily: tok.bodyFont, width: "100%", textAlign: "left",
          }}
        >
          Show me how →
        </button>
      ) : (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: "#FFF3D6",
              border: "1.5px dashed #E8C878",
              borderRadius: 12, padding: "14px 16px",
            }}
          >
            <p style={{ fontSize: 11, fontWeight: 700, color: "#C9870C", margin: "0 0 10px", textTransform: "uppercase", letterSpacing: ".06em", fontFamily: tok.labelFont }}>
              Walkthrough
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {walkthrough.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: "#C9870C",
                    background: "#FDEAB0", borderRadius: "50%",
                    width: 20, height: 20, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{i + 1}</span>
                  <p style={{ fontSize: 13 + a11ySize, color: "#8B5E0A", margin: 0, lineHeight: 1.6, fontFamily: tok.bodyFont }}>
                    <SmartText>{step}</SmartText>
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

// ── Shared: ScreenWidget ──────────────────────────────────────────────
function ScreenWidget({ screen, lesson, tok, accent }) {
  if (!screen.widget || screen.widget === "step_reveal") return null;
  const widgetType = normalizeWidgetType(screen.widget);
  const widgetObj = {
    type: widgetType,
    config: screen.widget_config ?? {},
  };
  const lessonId = lesson.id || "lesson";

  return (
    <div style={{ marginBottom: 16 }}>
      <ErrorBoundary fallback={
        <div style={{
          background: tok.card, border: `1px solid ${tok.border}`,
          borderRadius: tok.radius, padding: "14px 18px",
        }}>
          <p style={{ margin: 0, fontSize: 13, color: tok.textMid, fontFamily: tok.bodyFont }}>
            This interactive element couldn't load — tap Continue to keep going.
          </p>
        </div>
      }>
        <Suspense fallback={
          <div style={{
            background: tok.card, border: `1px solid ${tok.border}`,
            borderRadius: tok.radius, padding: 24, textAlign: "center",
          }}>
            <p style={{ color: tok.textDim, fontSize: 13, margin: 0 }}>Loading…</p>
          </div>
        }>
          <WidgetRenderer widget={widgetObj} lessonId={lessonId} mode="explore" />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

// ── LumiPresence (persistent orb with lumi nudge text) ────────────────
function LumiPresence({ lumiText, accent, tok }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (lumiText) {
      setOpen(true);
      const t = setTimeout(() => setOpen(false), 5000);
      return () => clearTimeout(t);
    }
  }, [lumiText]);

  if (!lumiText) return null;

  return (
    <div style={{
      position: "sticky", bottom: 0, zIndex: 10,
      pointerEvents: "none",
      display: "flex", justifyContent: "flex-end",
      padding: "0 16px 8px",
    }}>
      <AnimatePresence>
        {open && (
          <motion.div
            key={lumiText}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.95 }}
            style={{
              background: "#F7F5FF",
              border: "1px solid #E4DFF7",
              borderRadius: 14, padding: "10px 14px",
              maxWidth: 260,
              pointerEvents: "auto",
              cursor: "pointer",
              display: "flex", gap: 10, alignItems: "flex-start",
            }}
            onClick={() => setOpen(false)}
          >
            <div style={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0, marginTop: 1,
              background: `radial-gradient(circle at 35% 30%, #b9a6ff, #6C63FF 60%, #4B43C9)`,
              boxShadow: "0 2px 8px #6C63FF44",
            }} />
            <p style={{ fontSize: 12, color: "#4A3A80", margin: 0, lineHeight: 1.55, fontFamily: tok.bodyFont }}>
              {lumiText}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── LessonFooter ──────────────────────────────────────────────────────
const TEAL_GRAD = "linear-gradient(135deg, #19c4b3, #0E9488)";

function LessonFooter({ canContinue, onContinue, onBack, screenIndex, isLast, tok, accent }) {
  return (
    <div style={{
      padding: "12px 16px",
      background: tok.surface,
      borderTop: `1px solid ${tok.border}`,
      flexShrink: 0,
      display: "flex",
      gap: 10,
      paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
    }}>
      {screenIndex > 0 && (
        <button
          onClick={onBack}
          aria-label="Previous screen"
          style={{
            background: tok.card, border: `1.5px solid ${tok.border}`,
            borderRadius: 13, padding: "14px 18px",
            color: tok.textMid, fontWeight: 600, fontSize: 15,
            cursor: "pointer", flexShrink: 0, fontFamily: tok.bodyFont,
            transition: "all .2s",
          }}
        >←</button>
      )}
      <button
        onClick={canContinue ? onContinue : undefined}
        disabled={!canContinue}
        aria-label={isLast ? "Complete lesson" : "Continue to next screen"}
        style={{
          flex: 1, padding: "14px",
          background: canContinue ? TEAL_GRAD : tok.border,
          border: "none", borderRadius: 13,
          color: canContinue ? "white" : tok.textDim,
          fontWeight: 800, fontSize: 15,
          cursor: canContinue ? "pointer" : "not-allowed",
          transition: "all .2s",
          fontFamily: tok.bodyFont,
          boxShadow: canContinue ? "0 4px 14px rgba(14,148,136,.25)" : "none",
        }}
      >
        {isLast ? "Complete lesson ✓" : "Continue →"}
      </button>
    </div>
  );
}

// ── ScreenRouter ──────────────────────────────────────────────────────
function ScreenRouter({ screen, screenIndex, lesson, tok, accent, a11ySize, screenState, onAnswer, isFirstConcept }) {
  const rewardTone = lesson.reward_tone ?? (["playful", "friendly"].includes(lesson.presentation_mode) ? "playful" : "understated");

  const common = { screen, tok, accent, a11ySize };

  switch (screen.type) {
    case "concept":
      return <ConceptScreen {...common} lesson={lesson} isFirst={isFirstConcept} />;
    case "predict":
      return <PredictScreen {...common} screenState={screenState} onAnswer={onAnswer} />;
    case "explore":
      return <ExploreScreen {...common} lesson={lesson} />;
    case "reveal":
      return <RevealScreen {...common} lesson={lesson} />;
    case "check":
      return <CheckScreen {...common} screenState={screenState} onAnswer={onAnswer} />;
    case "construct":
      return <ConstructScreen {...common} screenState={screenState} onAnswer={onAnswer} />;
    case "worked":
      return <WorkedScreen {...common} />;
    case "challenge":
      return <ChallengeScreen {...common} lesson={lesson} screenState={screenState} onAnswer={onAnswer} />;
    case "celebrate":
      return <CelebrateScreen {...common} lesson={lesson} rewardTone={rewardTone} />;
    default:
      return (
        <div>
          <h2 style={{ fontSize: 18, color: tok.text, fontFamily: tok.headingFont }}>{screen.title}</h2>
          {screen.body && <p style={{ color: tok.textMid, fontFamily: tok.bodyFont }}>{screen.body}</p>}
        </div>
      );
  }
}

// ── Main player ───────────────────────────────────────────────────────
export default function LessonScreenPlayer({ lesson, lessonMeta, onClose }) {
  const screens = lesson.screens ?? [];
  const presentationMode = lesson.presentation_mode ?? "focused";
  const tok = getAgeTokens(presentationMode);
  const accent = lessonMeta?.color || lesson.color || "#6C63FF";

  // a11y font size bump
  const [a11ySize, setA11ySize] = useState(0);
  const [a11yOpen, setA11yOpen] = useState(false);

  // Review warmup gate
  const hasReview = lesson.review?.review_prompts?.length > 0 && isDue(lesson.review?.concept_id ?? "");
  const [reviewDone, setReviewDone] = useState(!hasReview);

  // Screen state — resume where the student left off in this lesson, if
  // recent enough (same 24h staleness window used elsewhere for resume).
  const screenPosKey = `lumio_screen_pos_${lesson.id || lessonMeta?.id}`;
  const [screenIndex, setScreenIndex] = useState(() => {
    try {
      const pos = JSON.parse(localStorage.getItem(screenPosKey) || 'null');
      if (pos && Number.isInteger(pos.idx) && pos.idx < screens.length && Date.now() - (pos.savedAt || 0) < 24 * 60 * 60 * 1000) {
        return pos.idx;
      }
    } catch (e) {}
    return 0;
  });
  const [screenStates, setScreenStates] = useState({});

  useEffect(() => {
    try { localStorage.setItem(screenPosKey, JSON.stringify({ idx: screenIndex, savedAt: Date.now() })); } catch (e) {}
  }, [screenIndex, screenPosKey]);

  const contentRef = useRef(null);

  // Scroll to top on screen change
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [screenIndex]);

  function getScreenState(idx) {
    return screenStates[idx] ?? { selected: null, attempts: 0, solved: false, walkthroughOpen: false };
  }

  function updateScreenState(idx, patch) {
    setScreenStates(prev => ({
      ...prev,
      [idx]: { ...getScreenState(idx), ...patch },
    }));
  }

  // Called when user picks an option (optionIdx) or opens walkthrough (openWalkthrough=true)
  // For construct screens, optionIdx is 0 (correct) or -1 (wrong), and isConstructCorrect is passed
  function handleAnswer(optionIdx, openWalkthrough = false, isConstructCorrect = false) {
    const state = getScreenState(screenIndex);
    const screen = screens[screenIndex];

    if (openWalkthrough) {
      updateScreenState(screenIndex, { walkthroughOpen: true });
      return;
    }

    if (screen.type === "construct") {
      if (isConstructCorrect) {
        updateScreenState(screenIndex, { solved: true });
        celebrate({ intensity: "normal" });
      } else {
        updateScreenState(screenIndex, { attempts: state.attempts + 1 });
      }
      return;
    }

    // Options-based answer
    const options = screen.options ?? [];
    if (optionIdx === null || optionIdx === undefined) return;
    const isCorrect = options[optionIdx]?.correct === true;

    if (isCorrect) {
      updateScreenState(screenIndex, { selected: optionIdx, solved: true });
      celebrate({ intensity: "normal" });
    } else {
      updateScreenState(screenIndex, {
        selected: optionIdx,
        attempts: state.attempts + 1,
      });
    }
  }

  const currentScreen = screens[screenIndex];
  const currentState = getScreenState(screenIndex);

  // Can continue?
  function canContinue() {
    if (!currentScreen) return false;
    if (!isGated(currentScreen.type)) return true;
    return currentState.solved || currentState.walkthroughOpen;
  }

  function handleContinue() {
    const isLast = screenIndex >= screens.length - 1;
    if (isLast) {
      // Complete lesson
      if (lesson.review?.concept_id) {
        scheduleReview(lesson.review.concept_id);
      }
      celebrate({ intensity: "big" });
      // Mark done in localStorage
      const key = `lumio_lesson_done_${lesson.id || lessonMeta?.id}`;
      localStorage.setItem(key, "1");
      try { localStorage.removeItem(screenPosKey); } catch (e) {}
      onClose?.();
    } else {
      setScreenIndex(i => i + 1);
    }
  }

  // A11y toolbar
  const A11yToolbar = () => (
    <AnimatePresence>
      {a11yOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          style={{
            position: "absolute", top: 60, right: 16, zIndex: 20,
            background: tok.surface, border: `1px solid ${tok.border}`,
            borderRadius: 12, padding: "12px 14px", boxShadow: "0 4px 16px #0004",
            display: "flex", alignItems: "center", gap: 10,
          }}
        >
          <span style={{ fontSize: 12, color: tok.textMid, fontFamily: tok.labelFont }}>Text size</span>
          {[-2, 0, 2, 4].map(size => (
            <button
              key={size}
              onClick={() => setA11ySize(size)}
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: a11ySize === size ? accent : tok.card,
                border: `1px solid ${a11ySize === size ? accent : tok.border}`,
                color: a11ySize === size ? "white" : tok.text,
                fontWeight: 700, fontSize: 12 + size / 2, cursor: "pointer",
              }}
            >A</button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!currentScreen) return null;

  const isFirstConcept = currentScreen.type === "concept" && screens.slice(0, screenIndex).every(s => s.type !== "concept");
  const isLastScreen = screenIndex >= screens.length - 1;

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: tok.bg, position: "relative",
    }}>
      <A11yToolbar />

      <LessonChrome
        screens={screens}
        screenIndex={reviewDone ? screenIndex : -1}
        lesson={{ ...lesson, color: accent }}
        onClose={onClose}
        tok={tok}
        a11ySize={a11ySize}
        onToggleA11y={() => setA11yOpen(o => !o)}
      />

      {!reviewDone ? (
        <ReviewWarmup lesson={lesson} tok={tok} onDone={() => setReviewDone(true)} />
      ) : (
        <>
          <div
            ref={contentRef}
            style={{ flex: 1, overflowY: "auto", padding: "20px 18px 8px" }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={screenIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.18 }}
              >
                <ScreenRouter
                  screen={currentScreen}
                  screenIndex={screenIndex}
                  lesson={lesson}
                  tok={tok}
                  accent={accent}
                  a11ySize={a11ySize}
                  screenState={currentState}
                  onAnswer={handleAnswer}
                  isFirstConcept={isFirstConcept}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Lumi nudge — shown inline above footer */}
          {currentScreen.lumi && (
            <LumiPresence
              lumiText={currentScreen.lumi}
              accent={accent}
              tok={tok}
            />
          )}

          <LessonFooter
            canContinue={canContinue()}
            onContinue={handleContinue}
            onBack={() => setScreenIndex(i => Math.max(0, i - 1))}
            screenIndex={screenIndex}
            isLast={isLastScreen}
            tok={tok}
            accent={accent}
          />
        </>
      )}
    </div>
  );
}
